import React, { useEffect, useState } from 'react';
import { EventForm } from './components/EventForm';
import { EventLibrary } from './components/EventLibrary';
import { Header } from './components/Header';
import { HomeScreen } from './components/HomeScreen';
import { SettingsScreen } from './components/SettingsScreen';
import { SaveMethodsMenu } from './components/SaveMethodsMenu';
import { PhotoUploader } from './components/PhotoUploader';
import { NotificationCenter } from './components/NotificationCenter';
import { ProfileEditScreen } from './components/ProfileEditScreen';
import { ChangePasswordScreen } from './components/ChangePasswordScreen';
import { ShareInstructions } from './components/ShareInstructions';
import { ShareModal } from './components/ShareModal';
import { SharedListView } from './components/SharedListView';
import { Toast } from './components/Toast';
import { HomeIcon, BookmarkIcon, UserIcon, PlusIcon, ChevronUpIcon } from 'lucide-react';
export function App() {
  const [events, setEvents] = useState([]);
  const [activeTab, setActiveTab] = useState('home');
  const [isAddingEvent, setIsAddingEvent] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const [showPhotoUploader, setShowPhotoUploader] = useState(false);
  const [ocrData, setOcrData] = useState(null);
  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  // Profile editing state
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  // Password change state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showShareInstructions, setShowShareInstructions] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSharedListView, setShowSharedListView] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  // Load events from localStorage on initial render
  useEffect(() => {
    const savedEvents = localStorage.getItem('wtm_events');
    if (savedEvents) {
      setEvents(JSON.parse(savedEvents));
    }
    // Load notifications from localStorage
    const savedNotifications = localStorage.getItem('wtm_notifications');
    if (savedNotifications) {
      setNotifications(JSON.parse(savedNotifications));
    } else {
      // Initialize with sample notifications
      setNotifications(getSampleNotifications());
    }
  }, []);
  // Save events to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('wtm_events', JSON.stringify(events));
  }, [events]);
  // Save notifications to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('wtm_notifications', JSON.stringify(notifications));
  }, [notifications]);
  // Generate sample notifications for demo purposes
  const getSampleNotifications = () => {
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    return [{
      id: 1,
      type: 'event',
      title: 'Event Reminder',
      message: 'Summer Music Festival is tomorrow! Get ready for an amazing day.',
      timestamp: now.toISOString(),
      read: false,
      action: 'View Event'
    }, {
      id: 2,
      type: 'registration',
      title: 'Registration Deadline',
      message: 'Only 2 days left to register for Tech Conference 2023.',
      timestamp: yesterday.toISOString(),
      read: false,
      action: 'Register Now'
    }, {
      id: 3,
      type: 'system',
      title: "Welcome to What's the Move",
      message: 'Thanks for joining! Start by adding your first event.',
      timestamp: yesterday.toISOString(),
      read: true
    }, {
      id: 4,
      type: 'reminder',
      title: 'Upcoming Event',
      message: 'Art Gallery Opening is happening this weekend.',
      timestamp: yesterday.toISOString(),
      read: true,
      action: 'View Details'
    }];
  };
  // Get count of unread notifications
  const getUnreadNotificationsCount = () => {
    return notifications.filter(notification => !notification.read).length;
  };
  // Mark a notification as read
  const markNotificationAsRead = id => {
    setNotifications(prevNotifications => prevNotifications.map(notification => notification.id === id ? {
      ...notification,
      read: true
    } : notification));
  };
  // Mark all notifications as read
  const markAllNotificationsAsRead = () => {
    setNotifications(prevNotifications => prevNotifications.map(notification => ({
      ...notification,
      read: true
    })));
  };
  const addEvent = event => {
    const newEvents = [...events, {
      ...event,
      id: Date.now(),
      isFavorite: false // Add favorite property to new events
    }];
    setEvents(newEvents);
    setIsAddingEvent(false);
    setOcrData(null);
    // Add a notification for the new event
    const newNotification = {
      id: Date.now(),
      type: 'system',
      title: 'Event Saved',
      message: `${event.name} has been added to your events.`,
      timestamp: new Date().toISOString(),
      read: false
    };
    setNotifications(prev => [newNotification, ...prev]);
  };
  const updateEvent = updatedEvent => {
    const newEvents = events.map(event => event.id === updatedEvent.id ? updatedEvent : event);
    setEvents(newEvents);
    setEditingEvent(null);
  };
  const deleteEvent = eventId => {
    const eventToDelete = events.find(event => event.id === eventId);
    const newEvents = events.filter(event => event.id !== eventId);
    setEvents(newEvents);
    // Add a notification for the deleted event
    if (eventToDelete) {
      const newNotification = {
        id: Date.now(),
        type: 'system',
        title: 'Event Deleted',
        message: `${eventToDelete.name} has been removed from your events.`,
        timestamp: new Date().toISOString(),
        read: false
      };
      setNotifications(prev => [newNotification, ...prev]);
    }
  };
  const toggleFavorite = eventId => {
    const newEvents = events.map(event => event.id === eventId ? {
      ...event,
      isFavorite: !event.isFavorite
    } : event);
    setEvents(newEvents);
    // Add a notification when an event is favorited
    const eventToggled = newEvents.find(event => event.id === eventId);
    if (eventToggled && eventToggled.isFavorite) {
      const newNotification = {
        id: Date.now(),
        type: 'event',
        title: 'Event Favorited',
        message: `${eventToggled.name} has been added to your favorites.`,
        timestamp: new Date().toISOString(),
        read: false
      };
      setNotifications(prev => [newNotification, ...prev]);
    }
  };
  const startEditingEvent = event => {
    setEditingEvent(event);
    setActiveTab('edit');
  };
  const cancelEditingEvent = () => {
    setEditingEvent(null);
    setActiveTab('saves');
  };
  const handleSaveMethodSelect = method => {
    setShowSaveMenu(false);
    if (method === 'share') {
      setShowShareInstructions(true);
    } else if (method === 'photo') {
      setShowPhotoUploader(true);
    } else if (method === 'manual') {
      setIsAddingEvent(true);
    }
  };
  const handleShareInstructionsContinue = () => {
    setShowShareInstructions(false);
    // Return to the Add Event menu so users can choose "Enter manually"
    setShowSaveMenu(true);
  };
  const handleImageCapture = data => {
    setShowPhotoUploader(false);
    setOcrData(data);
    setIsAddingEvent(true);
  };
  const handleAddButtonClick = () => {
    setShowSaveMenu(true);
  };
  const handleNotificationClick = () => {
    setShowNotifications(true);
  };
  const handleSettingsClick = () => {
    setShowProfileEdit(true);
  };
  const handleChangePassword = () => {
    setShowChangePassword(true);
  };
  const handleBackFromChangePassword = () => {
    setShowChangePassword(false);
  };
  const handleSignOut = () => {
    // In a real app, this would handle authentication logout
    console.log('User signed out');
    // For demo purposes, just navigate to home
    setActiveTab('home');
  };
  const handleShareList = () => {
    setShowShareModal(true);
  };
  const handleSaveSharedEvent = (eventName: string) => {
    setToastMessage('Added to your events');
    setShowToast(true);
  };
  const renderContent = () => {
    if (showShareInstructions) {
      return <ShareInstructions onClose={() => setShowShareInstructions(false)} onContinue={handleShareInstructionsContinue} data-id="element-0" />;
    }
    if (showNotifications) {
      return <NotificationCenter notifications={notifications} onClose={() => setShowNotifications(false)} onMarkAsRead={markNotificationAsRead} onMarkAllAsRead={markAllNotificationsAsRead} data-id="element-1" />;
    }
    if (showChangePassword) {
      return <ChangePasswordScreen onBack={handleBackFromChangePassword} data-id="element-2" />;
    }
    if (showProfileEdit) {
      return <ProfileEditScreen onBack={() => setShowProfileEdit(false)} onChangePassword={handleChangePassword} data-id="element-3" />;
    }
    if (showSaveMenu) {
      return <SaveMethodsMenu onSelectMethod={handleSaveMethodSelect} onCancel={() => setShowSaveMenu(false)} data-id="element-4" />;
    }
    if (showPhotoUploader) {
      return <PhotoUploader onImageCapture={handleImageCapture} onCancel={() => setShowPhotoUploader(false)} data-id="element-5" />;
    }
    if (isAddingEvent) {
      return <EventForm ocrData={ocrData} onSubmit={addEvent} onCancel={() => {
        setIsAddingEvent(false);
        setOcrData(null);
      }} data-id="element-6" />;
    }
    if (editingEvent) {
      return <EventForm event={editingEvent} isEditing={true} onSubmit={updateEvent} onCancel={cancelEditingEvent} data-id="element-7" />;
    }
    if (showSharedListView) {
      return <SharedListView onBack={() => setShowSharedListView(false)} onSaveEvent={handleSaveSharedEvent} data-id="element-8" />;
    }
    switch (activeTab) {
      case 'home':
        return <HomeScreen events={events} onEditEvent={startEditingEvent} onDeleteEvent={deleteEvent} onToggleFavorite={toggleFavorite} onShareList={handleShareList} data-id="element-9" />;
      case 'saves':
        return <EventLibrary events={events} onEditEvent={startEditingEvent} onDeleteEvent={deleteEvent} onToggleFavorite={toggleFavorite} data-id="element-10" />;
      case 'settings':
        return <SettingsScreen eventCount={events.length} onSignOut={handleSignOut} onEditProfile={() => setShowProfileEdit(true)} data-id="element-11" />;
      default:
        return <HomeScreen events={events} onEditEvent={startEditingEvent} onDeleteEvent={deleteEvent} onToggleFavorite={toggleFavorite} data-id="element-12" />;
    }
  };
  return <div className="flex flex-col min-h-screen w-full bg-gray-50" data-id="element-13">
      <Header activeTab={showChangePassword ? 'password' : showProfileEdit ? 'profile' : activeTab} unreadNotifications={getUnreadNotificationsCount()} onNotificationClick={handleNotificationClick} onSettingsClick={handleSettingsClick} data-id="element-14" />
      <main className="flex-1 container mx-auto px-4 py-4 max-w-lg mb-16" data-id="element-15">
        {renderContent()}
      </main>
      {/* Share Modal */}
      {showShareModal && <ShareModal onClose={() => setShowShareModal(false)} events={events} data-id="element-16" />}
      {/* Toast Notification */}
      <Toast message={toastMessage} isVisible={showToast} onDismiss={() => setShowToast(false)} data-id="element-17" />
      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 pb-safe" data-id="element-18">
        <div className="flex justify-around items-center h-16 max-w-lg mx-auto" data-id="element-19">
          <button onClick={() => {
          setActiveTab('home');
          setIsAddingEvent(false);
          setEditingEvent(null);
          setShowProfileEdit(false);
          setShowChangePassword(false);
        }} className={`flex flex-col items-center justify-center w-full h-full ${activeTab === 'home' ? 'text-blue-600' : 'text-gray-500'}`} data-id="element-20">
            <HomeIcon className="w-6 h-6" data-id="element-21" />
            <span className="text-xs mt-1" data-id="element-22">Home</span>
          </button>
          <button onClick={() => {
          setActiveTab('saves');
          setIsAddingEvent(false);
          setEditingEvent(null);
          setShowProfileEdit(false);
          setShowChangePassword(false);
        }} className={`flex flex-col items-center justify-center w-full h-full ${activeTab === 'saves' ? 'text-blue-600' : 'text-gray-500'}`} data-id="element-23">
            <BookmarkIcon className="w-6 h-6" data-id="element-24" />
            <span className="text-xs mt-1" data-id="element-25">Saves</span>
          </button>
          <button onClick={handleAddButtonClick} className="flex flex-col items-center justify-center w-full h-full" data-id="element-26">
            <div className="bg-blue-600 rounded-full p-3 -mt-6 flex items-center" data-id="element-27">
              <PlusIcon className="w-5 h-5 text-white" data-id="element-28" />
              <ChevronUpIcon className="w-4 h-4 text-white ml-1" data-id="element-29" />
            </div>
            <span className="text-xs mt-1 text-gray-500" data-id="element-30">Add</span>
          </button>
          <button onClick={() => {
          setActiveTab('settings');
          setIsAddingEvent(false);
          setEditingEvent(null);
          setShowProfileEdit(false);
          setShowChangePassword(false);
        }} className={`flex flex-col items-center justify-center w-full h-full ${activeTab === 'settings' ? 'text-blue-600' : 'text-gray-500'}`} data-id="element-31">
            <UserIcon className="w-6 h-6" data-id="element-32" />
            <span className="text-xs mt-1" data-id="element-33">Account</span>
          </button>
        </div>
      </nav>
    </div>;
}